import { Button, Col, Container, Spinner, Stack } from "react-bootstrap";
import React, { useEffect, useState } from "react";
import styles from "./ChangesCompanyDepartmentTemplate.module.scss";
import { useNavigate, useParams } from "react-router-dom";
import { ReactComponent as Arrow } from "assets/Icons/Arrow.svg";
import {
  useGetCompanyDepartmentQuery,
  usePatchCompanyDepartmentMutation,
} from "../../../redux/services/companyDepartmentApi";
import { ChangesContact } from "../../organisms/ChangesContact/ChangesContact";
import { ChangesCompanyDepartmentConfig } from "./ChangesCompanyDepartmentConfig";
import { Changes } from "../../organisms/Changes/Changes";
import { ChangesInfo } from "../../organisms/ChangesInfo/ChangesInfo";
import { countMatchingKeys } from "../../../Helpers/isExistKey";
import PopupModal from "../../molecules/PopupModal/PopupModal";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import {
  NotificationTypeEnum,
  usePostNotificationsMutation,
} from "../../../redux/services/notificationApi";
import { domain } from "../../organisms/Landing/Landing";
import { NAV_PATH } from "components/organisms/Navigation/navigationData";

const ChangesCompanyDepartmentTemplate: React.FC = () => {
  const { t } = useTranslation();
  const { id: companyDepartmentId } = useParams();
  const navigate = useNavigate();
  const { data: loyaltyProgramsCompanyDepartmentData, isLoading } =
    useGetCompanyDepartmentQuery(companyDepartmentId);
  const [showModal, setShowModal] = useState(false);

  const [patchCompanyDepartment] = usePatchCompanyDepartmentMutation();
  const [notification] = usePostNotificationsMutation();

  useEffect(() => {
    (async () => {
      if (!isLoading && changes) {
        await patchCompanyDepartment({
          id: companyDepartmentId,
          data: {
            changes: JSON.stringify({
              ...changes,
              startView: new Date().toISOString(),
            }),
          },
        });
      }
    })();
    // eslint-disable-next-line
  }, [isLoading]);

  if (isLoading) {
    return (
      <Container className={"d-flex justify-content-center"}>
        <Spinner animation="border" variant="primary" />
      </Container>
    );
  }

  const changes = JSON.parse(loyaltyProgramsCompanyDepartmentData.data.changes);

  const handleAcceptAll = async () => {
    const hasBeenUpdated = await patchCompanyDepartment({
      id: companyDepartmentId,
      data: { status: 1, changes: null },
    });
    if ("data" in hasBeenUpdated) {
      toast.success(t("toast_changesCompanyDepartmentTemplate_success"));
      if (loyaltyProgramsCompanyDepartmentData?.data?.company?.id) {
        await notification({
          data: {
            title: t(
              "notification_ChangesCompanyDepartmentTemplate_accept_title"
            ).slice(0, 50),
            type: NotificationTypeEnum["loyalty-program"],
            url: `${domain}/${NAV_PATH.SPONSOR}/${NAV_PATH.LOYALTY_PROGRAM}/${NAV_PATH.COMPANY}/${loyaltyProgramsCompanyDepartmentData?.data?.company?.id}`,
            viewSender: 0,
            senderUser: null,
            manyUser: "COMPANY",
            idMany: Number(
              loyaltyProgramsCompanyDepartmentData?.data?.company?.id
            ),
          },
        });
      }
      navigate(-1);
    } else if ("error" in hasBeenUpdated) {
      const error = hasBeenUpdated.error as TranslationSubmitError;
      if (error.data.statusCode === 500)
        toast.error(t("toast_changesCompanyDepartmentTemplate_error_500"));

      if (error.data.statusCode === 404)
        toast.error(t("toast_changesCompanyDepartmentTemplate_error_404"));

      if (error.data.statusCode === 400)
        toast.error(
          `${t("toast_changesCompanyDepartmentTemplate_error_400")} ${
            error.data.message[0]
          }`
        );
    }
  };

  const handleStatus = async () => {
    await patchCompanyDepartment({
      id: companyDepartmentId,
      data: {
        status: !loyaltyProgramsCompanyDepartmentData?.data.status ? 1 : 0,
      },
    });
  };

  const handleExit = async () => {
    const newChanges = { ...changes };
    delete newChanges.startView;
    await patchCompanyDepartment({
      id: companyDepartmentId,
      data: {
        changes: JSON.stringify(newChanges),
      },
    });
    navigate(-1);
  };

  const handleComments = async (e: string) => {
    const newChanges = {
      ...changes,
      comments: e,
      commentsDate: new Date().toISOString(),
    };
    await patchCompanyDepartment({
      id: companyDepartmentId,
      data: {
        changes: JSON.stringify(newChanges),
      },
    });
    if (loyaltyProgramsCompanyDepartmentData?.data?.company?.id) {
      await notification({
        data: {
          title: t(
            "notification_ChangesCompanyDepartmentTemplate_comments_title"
          ).slice(0, 50),
          type: NotificationTypeEnum["loyalty-program"],
          url: `${domain}/${NAV_PATH.SPONSOR}/${NAV_PATH.LOYALTY_PROGRAM}/${NAV_PATH.COMPANY}/${loyaltyProgramsCompanyDepartmentData?.data?.company?.id}`,
          viewSender: 0,
          senderUser: null,
          manyUser: "COMPANY",
          idMany: Number(
            loyaltyProgramsCompanyDepartmentData?.data?.company?.id
          ),
        },
      });
    }
  };

  const handlePatch = async (
    obj: { [key: string]: string },
    keyName: string
  ) => {
    const newChanges = { ...changes };
    delete newChanges[keyName];
    if (
      countMatchingKeys(
        changes,
        ChangesCompanyDepartmentConfig.map(
          (value: ChangesConfig) => value.keyName
        )
      ) === 1
    ) {
      await patchCompanyDepartment({
        id: companyDepartmentId,
        data: { ...obj, status: 1, changes: null },
      });
      if (loyaltyProgramsCompanyDepartmentData?.data?.company?.id) {
        await notification({
          data: {
            title: t(
              "notification_ChangesCompanyDepartmentTemplate_accept_title"
            ).slice(0, 50),
            type: NotificationTypeEnum["loyalty-program"],
            url: `${domain}/${NAV_PATH.SPONSOR}/${NAV_PATH.LOYALTY_PROGRAM}/${NAV_PATH.COMPANY}/${loyaltyProgramsCompanyDepartmentData?.data?.company?.id}#departments`,
            viewSender: 0,
            senderUser: null,
            manyUser: "COMPANY",
            idMany: Number(
              loyaltyProgramsCompanyDepartmentData?.data?.company?.id
            ),
          },
        });
      }
      navigate(-1);
    } else {
      const newOBJ = {
        id: companyDepartmentId,
        data: {
          ...obj,
          changes: JSON.stringify(newChanges),
        },
      };
      await patchCompanyDepartment(newOBJ);
    }
  };

  return (
    <Container fluid={true} className="bg-light">
      <section className={`${styles.section}`}>
        <Stack className={"w-100 p-0 m-0"} gap={5}>
          <Col
            className={"d-flex align-items-center cursor-pointer pb-4"}
            onClick={handleExit}
          >
            <Arrow
              style={{ transform: "rotate(180deg)" }}
              className={"text-primary"}
            />
            <span className={"ps-2 text-primary"}>
              WRÓĆ DO PRZEGLĄDANIA ZMIAN
            </span>
          </Col>
          <Col className={`${styles.wrapper}`}>
            <ChangesInfo
              changes={changes}
              status={loyaltyProgramsCompanyDepartmentData?.data.status}
              handleStatus={handleStatus}
              handleComments={handleComments}
              isAdmin={true}
            />
          </Col>
          {ChangesCompanyDepartmentConfig.map(
            // eslint-disable-next-line array-callback-return
            (value: ChangesConfig, index: number) => {
              if (value.type === "normal") {
                return (
                  <Changes
                    key={index}
                    keyName={value.keyName}
                    changes={changes}
                    data={loyaltyProgramsCompanyDepartmentData?.data}
                    name={value.name}
                    handlePatch={handlePatch}
                    handleComments={handleComments}
                    isAdmin={true}
                  />
                );
              } else if (value.type === "contact") {
                return (
                  <ChangesContact
                    key={index}
                    keyName={value.keyName}
                    changes={changes}
                    data={loyaltyProgramsCompanyDepartmentData?.data}
                    name={value.name}
                    handlePatch={handlePatch}
                    handleComments={handleComments}
                    isAdmin={true}
                  />
                );
              }
            }
          )}
          <div className={"d-flex gap-2 justify-content-end"}>
            <Button
              className={"fs-16 btn-outline-dark-blue"}
              onClick={handleExit}
            >
              WRÓĆ
            </Button>
            <Button
              className={"fs-16"}
              onClick={() => setShowModal((prev: boolean) => !prev)}
            >
              AKCEPTUJ CAŁOŚĆ
            </Button>
            <PopupModal
              text="Potwierdź całkowitą akceptację"
              smallText={"Po potwierdzeniu nie można powrócić"}
              type="confirm"
              show={showModal}
              setShow={() => setShowModal(false)}
              confirmAction={handleAcceptAll}
            />
          </div>
        </Stack>
      </section>
    </Container>
  );
};

export default ChangesCompanyDepartmentTemplate;
